// src/pages/SetPassword.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './SetPassword.css';

// Resolve backend URL for both Vite and CRA
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function SetPassword() {
  const navigate = useNavigate();

  const [token, setToken] = useState('');
  const [status, setStatus] = useState('verifying'); // verifying | ready | error | success
  const [userEmail, setUserEmail] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [err, setErr] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [shake, setShake] = useState(false);
  const [saving, setSaving] = useState(false);

  const tooShort = pw.length > 0 && pw.length < 8;
  const mismatch = pw2.length > 0 && pw !== pw2;
  const formValid = pw.length >= 8 && pw === pw2;

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('token') || '';
      setToken(t);

      async function verify() {
        try {
          const res = await fetch(
            `${API_BASE}/api/auth/verify-set-password?token=${encodeURIComponent(t)}`
          );
          const data = await res.json();
          if (data?.ok && data?.user?.email) {
            setUserEmail(data.user.email);
            setStatus('ready');
          } else {
            setStatus('error');
            setErr(data?.error || 'Invalid or expired link');
          }
        } catch {
          setStatus('error');
          setErr('Network error');
        }
      }

      if (t) verify();
      else {
        setStatus('error');
        setErr('Missing token');
      }
    } catch {
      setStatus('error');
      setErr('Invalid URL');
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setErr('');

    // We keep the button enabled and validate here so users see shake/alerts.
    if (!formValid) {
      if (mismatch) setErr('Passwords do not match.');
      if (tooShort) setErr('Password must be at least 8 characters.');
      setShake(true);
      setTimeout(() => setShake(false), 450);
      return;
    }

    try {
      setSaving(true);
      const res = await fetch(`${API_BASE}/api/auth/set-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: pw }),
      });
      const data = await res.json();
      if (data?.ok) {
        setStatus('success');
      } else {
        setErr(data?.error || 'Failed to set password');
      }
    } catch {
      setErr('Network error');
    } finally {
      setSaving(false);
    }
  }

  if (status === 'verifying') {
    return (
      <div className="setpw-page">
        <div className="setpw-card">
          <div className="setpw-status">Verifying link…</div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="setpw-page">
        <div className="setpw-card">
          <div className="setpw-status setpw-error">Error: {err}</div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="setpw-page">
        <div className="setpw-card">
          <h2 className="setpw-title">Password Set Successfully</h2>
          <p className="setpw-success">
            You can now{' '}
            <a className="setpw-link" href="https://analytics.cphere.ai">
              log in
            </a>.
          </p>
        </div>
      </div>
    );
  }

  // ready
  return (
    <div className="setpw-page">
      <div className={`setpw-card ${shake ? 'shake' : ''}`}>
        <h2 className="setpw-title">SET PASSWORD</h2>
        <p className="setpw-sub">
          ACCOUNT: <strong>{userEmail}</strong>
        </p>

        <form onSubmit={handleSubmit} noValidate>
          <div className="setpw-field">
            <label className="setpw-label">NEW PASSWORD</label>
            <input
              type={showPw ? 'text' : 'password'}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className={`setpw-input ${tooShort ? 'error' : ''}`}
              placeholder="AT LEAST 8 CHARACTERS"
              autoComplete="new-password"
              aria-invalid={tooShort ? 'true' : 'false'}
            />
            {tooShort && (
              <div className="setpw-error" aria-live="polite">
                Password must be at least 8 characters.
              </div>
            )}
          </div>

          <div className="setpw-field">
            <label className="setpw-label">CONFIRM PASSWORD</label>
            <input
              type={showPw ? 'text' : 'password'}
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className={`setpw-input ${mismatch ? 'error' : ''}`}
              placeholder="REPEAT PASSWORD"
              autoComplete="new-password"
              aria-invalid={mismatch ? 'true' : 'false'}
            />
            {mismatch && (
              <div className="setpw-error" aria-live="polite">
                Passwords do not match.
              </div>
            )}
          </div>

          <div className="setpw-row">
            <label className="setpw-toggle">
              <input
                id="showPw"
                type="checkbox"
                checked={showPw}
                onChange={(e) => setShowPw(e.target.checked)}
              />
              <span>SHOW PASSWORD</span>
            </label>
          </div>

          {err && <div className="setpw-error" style={{ marginTop: 8 }}>{err}</div>}

          {/* Centered actions to match modal */}
          <div className="setpw-actions">
            <button type="submit" className="setpw-button" disabled={saving}>
              {saving ? 'SAVING…' : 'SAVE PASSWORD'}
            </button>
            <button
              type="button"
              className="setpw-cancel"
              onClick={() => navigate('/login')}
              disabled={saving}
            >
              CANCEL
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
